import { Box, Button, Stack, TextField } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { Link } from "react-router-dom"
import { DataGrid } from "@core/components/dataGrid/dataGrid"
import { RefreshTableButton } from "../common/refreshTableButton"
import { usePostBotListState } from "../../hooks/usePostBotListState"
import { useGetTemplatesQuery } from "../../services/publications/templatesApi"
import type { Template } from "../../interface/publications"
import {
  readTemplateReturnContext,
  templatePath,
  type TemplateReturnContext,
} from "./templateNavigation"
import {
  templateDateLabel,
  TemplateQueryFeedback,
  useTemplateRowCount,
} from "./templateQueryFeedback"

export function TemplatesTable({
  listKey = "templates",
  returnContext,
}: {
  listKey?: string
  returnContext?: TemplateReturnContext
}) {
  const list = usePostBotListState({ resource: "templates", key: listKey })
  const query = useGetTemplatesQuery(list.listArgs, {
    refetchOnMountOrArgChange: true,
  })
  // A cached successful page is not a fresh reconciliation read.
  const page = !query.isFetching && !query.error ? query.currentData : undefined
  const rowCount = useTemplateRowCount(list.filters.search ?? "", page?.total)
  const columns = (
    [
      {
        field: "name",
        headerName: "Шаблон",
        minWidth: 240,
        flex: 1,
        renderCell: ({ row }) => (
          <Button
            component={Link}
            to={templatePath(row.id)}
            state={readTemplateReturnContext(returnContext)}
          >
            {row.name}
          </Button>
        ),
      },
      {
        field: "createdAt",
        headerName: "Создан (МСК)",
        width: 180,
        valueFormatter: ({ value }) => templateDateLabel(value),
      },
    ] satisfies GridColDef<Template>[]
  ).map((column) => ({ ...column, sortable: false, filterable: false }))
  return (
    <Stack spacing={2}>
      <TextField
        size="small"
        sx={{ width: { xs: "100%", sm: 300 }, maxWidth: "100%" }}
        label="Поиск шаблонов"
        value={list.filters.search ?? ""}
        onChange={(event) =>
          list.setFilters({ search: event.target.value || undefined })
        }
      />
      <TemplateQueryFeedback
        query={query}
        empty={page?.data.length === 0 && "Шаблонов нет"}
      />
      <RefreshTableButton
        label="Обновить список шаблонов"
        disabled={query.isFetching}
        onClick={() => void query.refetch()}
      />
      <Box sx={{ width: "100%", minHeight: 200 }}>
        <DataGrid
          {...list.gridProps}
          rows={page?.data ?? []}
          columns={columns}
          rowCount={rowCount}
          loading={query.isFetching}
          autoHeight
          disableRowSelectionOnClick
        />
      </Box>
    </Stack>
  )
}
